import React, { useContext, useEffect, useState } from "react";
import { RulesContext } from "../RulesContext";
import { CHANGE_RULE_WARNING } from "./RuleConstants";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Typography from "Components/DesignSystem/Typography/Typography";
import RuleChangeConfirmDialog from "./RuleChangeConfirmDialog";

/**
 * The CBI/CBA category row — "A ⟨category 1⟩ is compatible with ⟨category 2⟩ if" —
 * on the DS `Dropdown` (§ 112).
 *
 * It was two `react-select`s. `Dropdown` covers everything they were configured for:
 * `isSearchable` → `searchable`, grouped `[{label, options}]` → the flat `group` field
 * (§ 65), `menuPlacement="auto"` → `usePortal` (which also stops the listbox being
 * clipped by this card), `isClearable={false}` → the default, and
 * `IndicatorSeparator: () => null` → a chevron that never had a separator.
 *
 * **Three things this fixes that were invisible to every tool.**
 *
 * 1. **The card had no border.** Its class string was
 *    `tw-rounded-lg border border-[#E4E4E8] tw-bg-[#FAFAFC] …` — `border` and
 *    `border-[#E4E4E8]` carry **no `tw-` prefix**, and this project sets
 *    `prefix: 'tw-'`, so Tailwind generated nothing for either. The author wrote a
 *    border; the browser never drew one. Not a type error, not a lint error, not a
 *    failing test. Now `tw-border tw-border-edge`, which is what was meant.
 * 2. **`tw-bg-[#FAFAFC]` is a literal.** In dark mode that card stayed near-white
 *    with `ink` text over it. `surface-sunken` themes (#f4f5f7 light / #1d2125 dark)
 *    and is within a hair of the literal in light mode.
 * 3. **Both selects announced themselves as "RuleBody Status."** One copy-pasted
 *    `aria-label`, on two different controls, naming neither — a screen-reader user
 *    heard the same meaningless string twice. Each now carries its own name.
 *
 * Behaviour deliberately preserved to the letter: the row renders only once
 * `categoriesList` is non-empty; options are grouped by category type in
 * `categoryTypesList` order; `handleCategoryChange` still asks first when
 * `ruleState.rule` is non-empty and applies the change directly otherwise; the change
 * clears `compatibilityData`, dispatches `CBM_RULE_TYPE` with `[]`, and resets
 * `rule_body` to `{}`; and the `ruleBody` effect still seeds ids from
 * `ruleBody.leftCatID`/`rightCatID` and still blanks the stored `name` — see the
 * note on that effect below.
 *
 * The one thing that moved (§ 113): the confirm is `RuleChangeConfirmDialog` rather
 * than `Bootbox.confirm("Change Category?", …)`, so the pending choice waits in state
 * instead of in a Bootbox closure. `Common/BootBoxManager` is untouched — eleven other
 * consumers keep using it, two of them on this same page.
 */
const RuleCategorySelector = ({
  ruleBody,
  ruleDetails,
  setRuleDetails,
  ruleHeaderDetails,
  type
}) => {
  const {
    selectedCategories,
    setSelectedCategories,
    categoriesList,
    categoryTypesList,
    ruleState,
    setCompatibilityData,
    ruleDispatch,
  } = useContext(RulesContext);

  /**
   * Options for the DS `Dropdown`: one flat list whose `group` field buckets
   * consecutive entries (§ 65), rather than react-select's nested
   * `[{label, options: []}]`.
   *
   * Same shape of computation, same order — `categoryTypesList` drives the bucket
   * order and the categories keep their `categoriesList` order inside each bucket.
   * One difference, deliberate: a category type with **no** categories contributed
   * an `options: []` entry before, which react-select rendered as a heading with
   * nothing under it. Here it contributes no options and therefore no heading,
   * because a group label naming an empty group is noise.
   */
  function generateCategorySelectOptions() {
    const categoriesGroupbyCT = categoriesList?.reduce((acc, category) => {
      (acc[category.cattypeid] = acc[category.cattypeid] || []).push({
        label: category.name,
        value: category.id,
      });
      return acc;
    }, {});

    return (categoryTypesList ?? []).reduce((accCt, categoryType) => {
      const inType = categoriesGroupbyCT?.[categoryType.id] || [];
      inType.forEach((option) => {
        accCt.push({ ...option, group: categoryType.name });
      });
      return accCt;
    }, []);
  }

  const categoryOptions = generateCategorySelectOptions();

  /** The stored `name` is what `handle_change_categories` persists, so the label
   *  has to travel with the value — `Dropdown.onChange` hands over the value alone. */
  const labelFor = (value) =>
    categoryOptions.find((option) => option.value === value)?.label;

  /*
    Unchanged, including its quirk: this writes `name: ""` over whatever
    `RulesAddEditManage.load_Rule_Details` put there (`left_category_name` from the
    API), and reads `leftCatID`/`rightCatID` — camelCase keys the `rule/list`
    response does not use. It is pinned by a test that feeds a synthetic
    `ruleBody={{ leftCatID: 'cat-1' }}`, so it is preserved verbatim rather than
    "fixed" in a migration. Note the `Dropdown` no longer depends on the stored
    name: it resolves the label from `options` by value, so a blanked `name` no
    longer shows as an empty control.
  */
  useEffect(() => {
    if (ruleBody) {
      setSelectedCategories((prevState) => {
        return {
          ...prevState,
          leftCategory: { id: ruleBody.leftCatID, name: "" },
          rightCategory: { id: ruleBody.rightCatID, name: "" },
        };
      });
    }
  }, [ruleBody]);

  const handle_change_categories = (id, catId, catName) => {
    setCompatibilityData([]);
    ruleDispatch({ type: "CBM_RULE_TYPE", data: [] });
    setRuleDetails((prev) => {
      return {
        ...prev,
        ...ruleHeaderDetails,
        rule_body: {},
      };
    });

    setSelectedCategories((prevState) => {
      return {
        ...prevState,
        [id]: { id: catId, name: catName },
      };
    });
  };

  /**
   * The category the user picked but has not confirmed yet. `null` means no confirm is
   * pending. Holds `{ id, value, label }` — the same three arguments Bootbox used to
   * carry in its `[fn, id, value, label]` callback array.
   */
  const [pendingCategory, setPendingCategory] = useState(null);

  const handleCategoryChange = (id, value, label) => {
    if (ruleState && ruleState.rule.length !== 0) {
      setPendingCategory({ id, value, label });
    } else {
      handle_change_categories(id, value, label);
    }
  };

  const confirmCategoryChange = () => {
    handle_change_categories(
      pendingCategory.id,
      pendingCategory.value,
      pendingCategory.label
    );
    setPendingCategory(null);
  };

  /* `type === 'copy'` deliberately overrides `commit`: a copy starts life
     uncommitted, so its categories are editable even though the rule it was
     copied from is not. */
  const disabled = type === 'copy' ? false : Boolean(ruleDetails?.commit);

  /*
   * `justify-between` with each dropdown at a third of the row, less the gutter —
   * the arrangement the Bootstrap version produced, rebuilt without Bootstrap (§ 115).
   *
   * The first cut used `tw-gap-4` and `tw-w-[28%]`, which bunched all five items
   * against the left edge and left ~30% of the card empty on the right. The old markup
   * was `<div class="row tw-flex tw-justify-between tw-items-center">` holding two
   * `col-xl-4` wrappers and three BARE TEXT NODES — and `justify-between` is what
   * spread them across the full width.
   *
   * Measured against the real Bootstrap 5.0.0 sheet the app loads
   * (`public/includes/bootstrap-5-0-0/bootstrap.min.css`), on a 1398px card:
   *
   *   old:  A 17→26   d1 114→543   "is compatible with" 631→741   d2 845→1274   if 1370
   *
   * `col-xl-4` is 33.333% of the row and `.row > *` adds 12px of padding either side,
   * which is exactly what `calc(33.333% - 24px)` reproduces — 430.7px against the
   * old 429px on that card.
   *
   * The one thing NOT reproduced: the old wrappers carried `ms-2 me-2` and `ms-4 me-3`
   * — 8/8px on the first, 24/16px on the second. `justify-between` spaces MARGIN
   * boxes evenly (68px apiece, measured), so those mismatched margins pushed the
   * visible boxes around by different amounts and the four visible gaps came out
   * 76 / 76 / 92 / 84px. That asymmetry is accreted Bootstrap classes, not a design
   * decision, so the gaps here are even. The cost is that "is compatible with" lands
   * ~13px right of where it used to on a 1398px card, and nothing else moves by more
   * than 7px — under 1% of the card either way.
   *
   * `flex-wrap` is kept because `.row` is `flex-wrap: wrap`: on a narrow card the old
   * row wrapped rather than overflowing, and so does this one.
   */
  return (
    <div className="tw-rounded-lg tw-border tw-border-edge tw-bg-surface-sunken tw-py-6 tw-px-4">
      {categoriesList && categoriesList.length > 0 && (
        <div
          className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-4"
          data-testid="rule-category-row"
        >
          <Typography variant="body-primary" as="span" className="tw-text-ink">
            A
          </Typography>

          <div className="tw-w-[calc(33.333%-24px)]">
            <Dropdown
              label=""
              aria-label="Select Category 1"
              id="leftCategory"
              data-testid="rule-left-category-select"
              searchable
              searchPlaceholder="Search categories"
              usePortal
              options={categoryOptions}
              value={selectedCategories.leftCategory.id ?? ''}
              placeholder="Select Category 1"
              disabled={disabled}
              onChange={(value) =>
                handleCategoryChange("leftCategory", value, labelFor(value))
              }
            />
          </div>

          <Typography variant="body-primary" as="span" className="tw-text-ink">
            is compatible with
          </Typography>

          <div className="tw-w-[calc(33.333%-24px)]">
            <Dropdown
              label=""
              aria-label="Select Category 2"
              id="rightCategory"
              data-testid="rule-right-category-select"
              searchable
              searchPlaceholder="Search categories"
              usePortal
              options={categoryOptions}
              value={selectedCategories.rightCategory.id ?? ''}
              placeholder="Select Category 2"
              disabled={disabled}
              onChange={(value) =>
                handleCategoryChange("rightCategory", value, labelFor(value))
              }
            />
          </div>

          <Typography variant="body-primary" as="span" className="tw-text-ink">
            if
          </Typography>
        </div>
      )}
      {/* Rendered inside the card only for locality — `Dialog` portals to
          `document.body`, so it is neither clipped by this card nor affected by it. */}
      <RuleChangeConfirmDialog
        isOpen={pendingCategory !== null}
        title="Change Category?"
        message={CHANGE_RULE_WARNING}
        onConfirm={confirmCategoryChange}
        onCancel={() => setPendingCategory(null)}
        data-testid="rule-category-change-confirm"
      />
    </div>
  );
};

export default RuleCategorySelector;
